import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAccountBookStore } from '@/store/accountBookStore';
import SectionHeader from '../SectionHeader';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0,0,0,0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0,0,0,0.3)',
    }
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: 'var(--bg-elev-2)',
  '& .MuiTableCell-head': {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: '0.95rem',
    textAlign: 'center',
    border: `1px solid ${theme.palette.divider}`,
    padding: '14px 8px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'var(--bg-muted)',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableCell-body': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '12px 8px',
    fontSize: '0.9rem',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'left',
  fontWeight: 500,
}));

const NumberCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'right',
  fontWeight: 600,
  fontFamily: 'var(--font-numeric)',
}));

const CategoryAnalysisSection: React.FC = () => {
  const theme = useTheme();
  const { dashboardData } = useAccountBookStore();

  const formatNumber = (num: number): string => {
    return num.toLocaleString('ko-KR');
  };

  const totalRevenue = dashboardData.kpiData.totalIncome;
  const totalExpense = dashboardData.kpiData.totalExpense;

  // 카테고리별 매출과 지출을 합쳐서 분석
  const categoryMap = new Map<string, { revenue: number; expense: number; category: string }>();

  // 매출 데이터 추가
  dashboardData.revenueSources.forEach(item => {
    const category = item.category || '기타';
    if (!categoryMap.has(category)) {
      categoryMap.set(category, { revenue: 0, expense: 0, category });
    }
    categoryMap.get(category)!.revenue = item.amount;
  });

  // 지출 데이터 추가
  dashboardData.categoryExpenses.forEach(item => {
    const category = item.category || '기타';
    if (!categoryMap.has(category)) {
      categoryMap.set(category, { revenue: 0, expense: 0, category });
    }
    categoryMap.get(category)!.expense = item.amount;
  });

  // 배열로 변환 및 정렬 (총액 기준)
  const categoryData = Array.from(categoryMap.values())
    .map(item => ({
      ...item,
      total: item.revenue + item.expense,
      expenseRatio: totalRevenue > 0 ? (item.expense / totalRevenue) * 100 : 0,
    }))
    .sort((a, b) => b.total - a.total);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 2, sm: 3 },
        background: 'var(--bg-base)',
        borderRadius: 'var(--r-16)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <SectionHeader
        eyebrow="카테고리 분포"
        title="카테고리별 분석"
        subtitle={`${dashboardData.selectedYear}년 카테고리 매출·지출 비중`}
      />

      <Grid container spacing={2}>
        {/* 카테고리별 매출 테이블 */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
            카테고리별 매출
          </Typography>
          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell>카테고리</StyledTableCell>
                  <NumberCell>금액</NumberCell>
                  <NumberCell>비율 (%)</NumberCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {dashboardData.revenueSources.length > 0 ? (
                  dashboardData.revenueSources.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{item.category || '기타'}</StyledTableCell>
                      <NumberCell sx={{ color: 'success.main' }}>
                        {formatNumber(item.amount)}
                      </NumberCell>
                      <NumberCell>{item.percentage.toFixed(2)}%</NumberCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                      매출 데이터가 없습니다
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Grid>

        {/* 카테고리별 지출 테이블 */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
            카테고리별 지출
          </Typography>
          <StyledTableContainer>
            <Table size="small">
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell>카테고리</StyledTableCell>
                  <NumberCell>금액</NumberCell>
                  <NumberCell>비율 (%)</NumberCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {dashboardData.categoryExpenses.length > 0 ? (
                  dashboardData.categoryExpenses.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{item.category || '기타'}</StyledTableCell>
                      <NumberCell sx={{ color: 'error.main' }}>
                        {formatNumber(item.amount)}
                      </NumberCell>
                      <NumberCell>{item.percentage.toFixed(2)}%</NumberCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                      지출 데이터가 없습니다
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Grid>

        {/* 통합 분석 테이블 */}
        {categoryData.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              카테고리별 통합 분석 (매출 대비 지출 비율)
            </Typography>
            <StyledTableContainer>
              <Table size="small">
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell>카테고리</StyledTableCell>
                    <NumberCell>매출</NumberCell>
                    <NumberCell>지출</NumberCell>
                    <NumberCell>지출/매출 비율 (%)</NumberCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {categoryData.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{item.category}</StyledTableCell>
                      <NumberCell sx={{ color: 'success.main' }}>
                        {formatNumber(item.revenue)}
                      </NumberCell>
                      <NumberCell sx={{ color: 'error.main' }}>
                        {formatNumber(item.expense)}
                      </NumberCell>
                      <NumberCell 
                        sx={{ 
                          color: item.expenseRatio > 100 ? 'error.main' : 'text.primary',
                          fontWeight: item.expenseRatio > 100 ? 700 : 600
                        }}
                      >
                        {item.expenseRatio.toFixed(2)}%
                      </NumberCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default CategoryAnalysisSection;





